import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.webp';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './SafetyManagementSoftware.css';

const SUPPORT_AREAS = [
  { text: 'WHS and compliance documents', icon: 'folder' },
  { text: 'Digital forms and QR codes', icon: 'form' },
  { text: 'Workplace inspections and audits', icon: 'audit' },
  { text: 'Hazards and incidents', icon: 'hazard' },
  { text: 'Corrective actions', icon: 'task' },
  { text: 'Tasks and projects', icon: 'project' },
  { text: 'SWMS and risk assessments', icon: 'shield' },
  { text: 'Employee and contractor records', icon: 'users' },
  { text: 'Sites and locations', icon: 'pin' },
  { text: 'Plant and asset registers', icon: 'plant' },
  { text: 'Training and induction records', icon: 'training' },
  { text: 'Compliance reporting', icon: 'report' },
];

const HELP_FEATURES = [
  {
    title: 'Manage WHS Documents',
    icon: 'folder',
    paragraphs: [
      'Centralise policies, procedures, SWMS, risk assessments, WHS plans and other compliance records.',
      'You can organise documents, maintain versions, track review and expiry dates, and search the document library when information is needed.',
    ],
  },
  {
    title: 'Complete Digital WHS Forms',
    icon: 'form',
    paragraphs: [
      <>
        Create and manage{' '}
        <Link
          to={APP_URLS.DIGITAL_WHS_FORMS}
          className="section-description-link"
        >
          digital WHS forms
        </Link>
        {' '}with QR codes, making it easier for workers to complete safety checks
        and submit information from the workplace.
      </>,
      'This can support site inductions, toolbox talks, pre-start checklists, hazard reports and incident reporting without relying on paper forms.',
    ],
  },
  {
    title: 'Run Audits and Inspections',
    icon: 'audit',
    paragraphs: [
      <>
        Run regular{' '}
        <Link
          to={APP_URLS.WHS_COMPLIANCE_AUDIT_SOFTWARE}
          className="section-description-link"
        >
          WHS compliance audits
        </Link>, identify issues and track findings so your team can maintain
        consistent safety standards across the workplace.
      </>,
      'Complimate also includes clause-cited audit functionality and AI-assisted document and photo review.',
    ],
  },
  {
    title: 'Track Corrective Actions',
    icon: 'task',
    paragraphs: [
      'Turn findings into assigned tasks with owners, priorities, due dates, attachments, comments and status tracking.',
      'Teams can use project and task workflows to monitor actions from To Do through In Progress to Completed.',
    ],
  },
];

const FEATURE_TABLE = [
  {
    feature: 'WHS Documents',
    icon: 'folder',
    helps: 'Policies, procedures, SWMS, registers and compliance records',
  },
  {
    feature: 'Digital Forms & QR',
    icon: 'form',
    helps: 'Paperless forms, inspections and site submissions',
  },
  {
    feature: 'Audits',
    icon: 'audit',
    helps: 'Audits, findings and supporting evidence',
  },
  {
    feature: 'Corrective Actions',
    icon: 'task',
    helps: 'Assigned actions, deadlines and closure',
  },
  {
    feature: 'Incidents & Hazards',
    icon: 'hazard',
    helps: 'Digital safety reporting and records',
  },
  {
    feature: 'Sites',
    icon: 'pin',
    helps: 'Site-level documents and submitted forms',
  },
  {
    feature: 'Assets & Registers',
    icon: 'plant',
    helps: 'Vehicles, plant, equipment and tools',
  },
  {
    feature: 'Employees & Roles',
    icon: 'users',
    helps: 'Responsibilities, employee records and access permissions',
  },
  {
    feature: 'Dashboard & Alerts',
    icon: 'dashboard',
    helps: 'Tasks, projects, documents and notifications',
  },
  {
    feature: 'Mobile Apps',
    icon: 'mobile',
    helps: 'Field access, forms, photos, documents and AI assistance',
  },
];

const AUDIENCE = [
  { text: 'WHS and HSE Managers', icon: 'shield' },
  { text: 'Compliance Officers', icon: 'audit' },
  { text: 'Quality Managers', icon: 'report' },
  { text: 'Site Supervisors', icon: 'hardhat' },
  { text: 'Operations Managers', icon: 'dashboard' },
  { text: 'Consultants supporting client compliance', icon: 'users' },
];

const FAQ_ITEMS = [
  {
    question: 'What is safety management software?',
    answer:
      'Safety management software is used to organise workplace safety activities, documents, inspections, audits, incidents, corrective actions and related compliance records digitally.',
  },
  {
    question: 'Is Complimate a WHS software platform?',
    answer:
      'Yes. Complimate is positioned as an Australian WHS and compliance management platform covering documents, digital forms, audits, hazards, incidents, corrective actions, tasks, sites, assets, employees and other safety workflows.',
  },
  {
    question: 'Can Complimate manage WHS documents?',
    answer:
      'Yes. The document module supports policies, procedures, SWMS, registers, WHS plans and compliance records, including document versioning and expiry tracking.',
  },
  {
    question: 'Can workers use Complimate from mobile devices?',
    answer:
      'Yes. Complimate has Android and iOS apps that support core compliance activities such as documents, tasks, digital forms, photo evidence and AI-assisted answers.',
  },
  {
    question: 'Can Complimate replace our legal or WHS advice?',
    answer:
      'No. Complimate is a software platform that helps businesses manage and document their WHS and compliance processes. AI-generated recommendations and documents should not be presented as legal advice.',
  },
  {
    question: 'Does Complimate guarantee WHS compliance?',
    answer:
      'No. The platform helps businesses manage WHS compliance, organise evidence and support their safety management processes. It does not guarantee that a business is compliant or prevent every workplace incident.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/safety-management-software/');

const SYSTEM_FLOW = [
  { label: 'Documents', icon: 'folder' },
  { label: 'Forms', icon: 'form' },
  { label: 'Inspections', icon: 'audit' },
  { label: 'Findings', icon: 'search' },
  { label: 'Corrective Actions', icon: 'task' },
  { label: 'Tasks', icon: 'project' },
  { label: 'Evidence', icon: 'photo' },
  { label: 'Records', icon: 'report' },
];

const ICON_PATHS = {
  shield: (
    <>
      <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />
      <path d="m8.5 12 2.2 2.2 4.8-5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  audit: (
    <>
      <path d="M8 4h8M9 3v3h6V3M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5M12 17h.01" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  project: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2v4M16 2v4M3 9h18M7 13h4M7 17h8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  training: (
    <>
      <path d="m2 8 10-5 10 5-10 5L2 8Z" />
      <path d="M6 10.5V16c3.5 2.6 8.5 2.6 12 0v-5.5M22 8v7" />
    </>
  ),
  report: (
    <>
      <path d="M6 3h8l4 4v14H6V3ZM14 3v5h5" />
      <path d="M9 17v-3M12 17v-6M15 17v-4" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  hardhat: (
    <>
      <path d="M4 14a8 8 0 0 1 16 0M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3ZM12 4v3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </>
  ),
  photo: (
    <>
      <rect x="3" y="5" width="18" height="15" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 16-5-5L5 20" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="sms-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.shield}
      </svg>
    </span>
  );
}

function SafetyManagementSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="sms-page">
        <section className="sms-hero">
          <Container>
            <div className="sms-hero-grid">
              <div className="sms-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="shield" />
                  Safety Management Software
                </p>
                <h1 className="seo-h1">
                  Safety Management Software for Australian Businesses
                </h1>
                <p className="seo-lead">
                  Complimate helps Australian businesses manage workplace safety,
                  documentation, audits and compliance with one connected platform.
                  It combines{' '}
                  <Link
                    to={APP_URLS.WHS_COMPLIANCE_SOFTWARE}
                    className="section-description-link"
                  >
                    WHS compliance software
                  </Link>
                  {' '}with practical safety management workflows to help teams stay
                  organised and audit-ready.
                </p>
                <p className="seo-body">
                  Built for teams that need a practical way to manage their safety
                  management systems, Complimate connects day-to-day safety
                  activities with the documents, people, sites and actions behind
                  them.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal sms-primary-button"
                    id="sms_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>
              <div className="sms-hero-visual">
                <div className="sms-visual-glow" aria-hidden="true" />
                <div className="sms-product-window">
                  <div className="sms-window-bar" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate safety management dashboard for WHS tasks, documents and alerts"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="sms-float-card sms-float-card-top" aria-hidden="true">
                  <FeatureIcon name="audit" />
                  <span>Audits &amp; inspections</span>
                </div>
                <div className="sms-float-card sms-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="task" />
                  <span>Corrective actions</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section sms-intro-section">
          <Container>
            <div className="sms-intro-grid">
              <div>
                <div className="sms-section-marker"><FeatureIcon name="shield" /></div>
                <h2 className="seo-h2">What Is Safety Management Software?</h2>
                <p className="seo-body">
                  Safety management software is a digital platform used to organise
                  and manage workplace health and safety activities, records and
                  compliance tasks.
                </p>
                <p className="seo-body">
                  Instead of managing safety information across spreadsheets, shared
                  folders, paper forms and emails, teams can use Complimate to bring
                  key WHS workflows together.
                </p>
                <p className="seo-body">Complimate supports areas including:</p>
              </div>
              <div className="sms-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate WHS document and compliance record library"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="sms-support-grid">
              {SUPPORT_AREAS.map((item) => (
                <li className="sms-support-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body sms-centered-note">
              This makes Complimate suitable for businesses that want to manage
              their WHS software and safety compliance processes in one place.
              These capabilities are documented as current product functionality.
            </p>
          </Container>
        </section>

        <section className="seo-section sms-help-section">
          <Container>
            <div className="sms-help-intro">
              <div className="sms-shot-frame">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital WHS forms and safety workflow"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="sms-section-marker"><FeatureIcon name="audit" /></div>
                <h2 className="seo-h2">
                  How Can Complimate Help Manage Workplace Safety?
                </h2>
                <p className="seo-body">
                  Complimate connects different parts of your safety workflow
                  instead of treating each activity as a separate checklist.
                </p>
                <p className="seo-body">
                  For example, a hazard identified during an inspection can lead to
                  a corrective action, assigned responsibility, a task and a
                  compliance record. This creates a clearer path from finding an
                  issue to tracking its resolution.
                </p>
              </div>
            </div>
            <div className="sms-help-grid">
              {HELP_FEATURES.map((item, index) => (
                <article className="sms-help-card" key={item.title}>
                  <div className="sms-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={`${item.title}-${paragraphIndex}`} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section sms-cta-section">
          <Container>
            <div className="sms-cta-card">
              <span className="sms-cta-orb sms-cta-orb-one" aria-hidden="true" />
              <span className="sms-cta-orb sms-cta-orb-two" aria-hidden="true" />
              <div className="sms-cta-copy">
                <h2 className="seo-h2">Manage Your Safety System in One Place</h2>
                <p className="seo-body">
                  Use Complimate safety management software to bring WHS documents,
                  forms, audits, corrective actions, tasks, sites and compliance
                  records together in one practical platform for Australian
                  businesses.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal sms-cta-button"
                id="sms_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section sms-features-section">
          <Container>
            <div className="sms-section-intro">
              <h2 className="seo-h2">What Features Are Included?</h2>
              <p className="seo-body">
                Complimate combines several safety and compliance workflows in one
                platform.
              </p>
            </div>
            <div className="seo-table-wrap sms-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">What it helps you manage</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_TABLE.map((row) => (
                    <tr key={row.feature}>
                      <th scope="row">
                        <span className="sms-table-feature">
                          <FeatureIcon name={row.icon} />
                          {row.feature}
                        </span>
                      </th>
                      <td>{row.helps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              The functionality brief identifies these modules as part of the live
              Complimate product.
            </p>
          </Container>
        </section>

        <section className="seo-section sms-audience-section">
          <Container>
            <div className="sms-audience-intro">
              <div>
                <div className="sms-section-marker"><FeatureIcon name="users" /></div>
                <h2 className="seo-h2">
                  Who Is Complimate Safety Management Software For?
                </h2>
                <p className="seo-body">
                  Complimate is designed for Australian businesses that need to
                  manage WHS and compliance across people, documents, tasks and
                  locations.
                </p>
                <p className="seo-body">It is particularly relevant to:</p>
              </div>
              <div className="sms-shot-frame">
                <img
                  src={isDark ? DarkManageUser : LightManageUser}
                  alt="Complimate employee roles and access for safety teams"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="sms-audience-grid">
              {AUDIENCE.map((item) => (
                <li className="sms-audience-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <strong>{item.text}</strong>
                </li>
              ))}
            </ul>
            <p className="seo-body sms-centered-note">
              The platform&apos;s target industries include construction,
              childcare, health, not-for-profit and local government.
            </p>
          </Container>
        </section>

        <section className="seo-section sms-system-section">
          <Container>
            <div className="sms-system-card">
              <div className="sms-section-intro">
                <h2 className="seo-h2">
                  How Does Complimate Support Safety Management Systems?
                </h2>
                <p className="seo-body">
                  A safety management system brings together the processes a
                  business uses to manage workplace safety.
                </p>
                <p className="seo-body">
                  Complimate can support these processes by connecting:
                </p>
              </div>
              <div className="sms-flow" aria-label="Safety management workflow">
                {SYSTEM_FLOW.map((item, index) => (
                  <div className="sms-flow-step" key={item.label}>
                    <FeatureIcon name={item.icon} />
                    <strong>{item.label}</strong>
                    {index < SYSTEM_FLOW.length - 1 && (
                      <span className="sms-flow-arrow" aria-hidden="true">→</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="seo-body">
                This connected workflow is one of the platform&apos;s key
                positioning points. Complimate was developed alongside an active
                Australian WHS consulting business, with the product designed
                around practical WHS management rather than only software
                functionality.
              </p>
              <div className="sms-note">
                <FeatureIcon name="info" />
                <p className="seo-body mb-0">
                  Complimate supports safety management systems; it does not by
                  itself guarantee that a business is legally compliant.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section sms-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default SafetyManagementSoftware;
